// REACT
import { useEffect, useMemo, useState } from "react";

// MATERIAL UI
import {
  Box,
  ButtonBase,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  styled,
} from "@mui/material";

// COMPONENTS
import ContainedButton from "src/components/common/ContainedButton";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import ProvideCostBasisModal from "./ProvideCostBasisModal";

// REDUX
import api from "src/store/features/api";
import { TaxBitTransaction } from "src/store/features/api/generated";
import { useAppDispatch } from "src/store/store";

// STYLES & UTILS
import {
  BORDER,
  BORDER_LIGHT,
  Card,
  CountPill,
  NAVY,
  SALMON,
  TEXT_MUTED,
  TEXT_SECONDARY,
} from "./styles";
import { buildTaxTransactionsArg, formatUnits, formatUsd, formatUtcDateTime } from "./utils";

const PAGE_SIZE = 8;

const TYPE_LABELS: Record<string, string> = {
  "transfer-in": "Transfer in",
  deposit: "Transfer in",
  "transfer-out": "Transfer out",
  withdraw: "Transfer out",
  buy: "Buy",
  sell: "Sell",
  trade: "Trade",
  income: "Income",
  reward: "Reward",
  airdrop: "Airdrop",
  expense: "Expense",
};

const isTransferOut = (type?: string) =>
  type === "transfer-out" || type === "withdraw" || type === "withdrawal";

const typeLabel = (type?: string) =>
  (type && TYPE_LABELS[type.toLowerCase()]) ?? (type ? type.replace(/-/g, " ") : "—");

const signedUsd = (amount?: string) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return formatUsd(amount);
  return `${value > 0 ? "+" : ""}${formatUsd(value)}`;
};

type ReviewTab = "needs-review" | "declared";

const TABS: { id: ReviewTab; label: string }[] = [
  { id: "needs-review", label: "Needs review" },
  { id: "declared", label: "Declared" },
];

const TabButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active: boolean }>(({ active }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "16px 12px",
  borderBottom: `2px solid ${active ? SALMON : "transparent"}`,
  fontFamily: "Roobert, sans-serif",
  fontSize: 15,
  fontWeight: active ? 500 : 400,
  color: active ? NAVY : TEXT_MUTED,
}));

const HeaderCell = styled(TableCell)({
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  fontSize: 12,
  fontWeight: 500,
  color: TEXT_MUTED,
  backgroundColor: "#FBFCFC",
  borderBottom: `1px solid ${BORDER}`,
  padding: "12px 24px",
});

const BodyCell = styled(TableCell)({
  fontSize: 14,
  color: NAVY,
  borderBottom: `1px solid ${BORDER_LIGHT}`,
  padding: "16px 24px",
});

const PageButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active: boolean }>(({ active }) => ({
  width: 30,
  height: 30,
  border: `1px solid ${BORDER}`,
  borderRadius: 4,
  fontFamily: "Roobert, sans-serif",
  fontSize: 13,
  fontWeight: active ? 500 : 400,
  color: active ? NAVY : TEXT_MUTED,
}));

interface TaxTransactionReviewTableProps {
  taxYear: number;
}

const TaxTransactionReviewTable = ({ taxYear }: TaxTransactionReviewTableProps) => {
  const dispatch = useAppDispatch();
  const queryArg = useMemo(() => buildTaxTransactionsArg(taxYear), [taxYear]);
  const transactionsQuery = api.useGetTaxTransactionsQuery(queryArg, { refetchOnFocus: false });
  // CountryOrCryptoFlag needs a logo URL; TaxBit only gives us the asset code.
  const { data: cryptoCurrencies } = api.useGetCryptoCurrenciesQuery();
  const logoByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const currency of cryptoCurrencies ?? []) {
      if (currency.code && currency.logoUrl) map.set(currency.code, currency.logoUrl);
    }
    return map;
  }, [cryptoCurrencies]);

  const [activeTab, setActiveTab] = useState<ReviewTab>("needs-review");
  const [page, setPage] = useState(0);
  const [selectedTransaction, setSelectedTransaction] = useState<TaxBitTransaction | null>(null);

  useEffect(() => setPage(0), [activeTab, taxYear]);

  const allTransactions = useMemo(
    () =>
      [...(transactionsQuery.data?.data ?? [])].sort(
        (a, b) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime(),
      ),
    [transactionsQuery.data],
  );

  const counts = useMemo(
    () => ({
      "needs-review": allTransactions.filter((item) => item.missing_cost_basis).length,
      declared: allTransactions.filter((item) => !item.missing_cost_basis).length,
    }),
    [allTransactions],
  );

  const tabTransactions = useMemo(
    () =>
      allTransactions.filter((item) =>
        activeTab === "needs-review" ? item.missing_cost_basis : !item.missing_cost_basis,
      ),
    [activeTab, allTransactions],
  );

  const pageCount = Math.max(1, Math.ceil(tabTransactions.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageTransactions = tabTransactions.slice(
    currentPage * PAGE_SIZE,
    (currentPage + 1) * PAGE_SIZE,
  );

  const closeReviewModal = () => setSelectedTransaction(null);

  const handleSubmitted = (transactionId: string) => {
    dispatch(
      api.util.updateQueryData("getTaxTransactions", queryArg, (draft) => {
        const transaction = draft.data.find((item) => item.id === transactionId);
        if (!transaction) return;
        transaction.missing_cost_basis = false;
        transaction.has_transfer_lot_data = true;
      }),
    );
    closeReviewModal();
  };

  // The modal deleted this transaction's existing lots but the replacement create failed, so
  // TaxBit now has none — patch the cache so it shows as needing review instead of still
  // looking declared. Deliberately doesn't close the modal: the user's entered values are
  // still on screen there and a retry is safe (see ProvideCostBasisModal's handleComplete).
  const handleReplacementFailed = (transactionId: string) => {
    dispatch(
      api.util.updateQueryData("getTaxTransactions", queryArg, (draft) => {
        const transaction = draft.data.find((item) => item.id === transactionId);
        if (!transaction) return;
        transaction.missing_cost_basis = true;
        transaction.has_transfer_lot_data = false;
      }),
    );
  };

  return (
    <Card>
      <Stack direction="row" gap={0.5} sx={{ px: 2.5, borderBottom: `1px solid ${BORDER}` }}>
        {TABS.map((tab) => (
          <TabButton
            key={tab.id}
            active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            <CountPill tone={tab.id === "needs-review" && counts[tab.id] > 0 ? "error" : "neutral"}>
              {counts[tab.id]}
            </CountPill>
          </TabButton>
        ))}
      </Stack>

      <Box sx={{ overflowX: "auto" }}>
        <Table aria-label="tax transaction review table" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <HeaderCell>Asset</HeaderCell>
              <HeaderCell>Type</HeaderCell>
              <HeaderCell>Quantity</HeaderCell>
              <HeaderCell>Date (UTC)</HeaderCell>
              <HeaderCell>Cost basis</HeaderCell>
              <HeaderCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {transactionsQuery.isLoading &&
              Array.from({ length: 4 }).map((_, idx) => (
                <TableRow key={`skeleton-${idx}`}>
                  <BodyCell colSpan={6}>
                    <Skeleton variant="text" height={26} />
                  </BodyCell>
                </TableRow>
              ))}

            {!transactionsQuery.isLoading && pageTransactions.length === 0 && (
              <TableRow>
                <BodyCell colSpan={6}>
                  <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY, py: 1 }}>
                    {transactionsQuery.error
                      ? "Unable to load transactions right now. Please try again shortly."
                      : "No transactions found for this filter."}
                  </Typography>
                </BodyCell>
              </TableRow>
            )}

            {!transactionsQuery.isLoading &&
              pageTransactions.map((transaction) => {
                const canReview =
                  transaction.missing_cost_basis || transaction.has_transfer_lot_data;
                return (
                  <TableRow key={transaction.id}>
                    <BodyCell>
                      <Stack direction="row" alignItems="center" gap={1.5}>
                        <Box sx={{ width: 30, height: 30, flex: "none" }}>
                          <CountryOrCryptoFlag
                            name={
                              logoByCode.get(transaction.asset_amount?.asset?.code ?? "") ??
                              transaction.asset_amount?.asset?.code
                            }
                            size={30}
                          />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 500, color: NAVY }}>
                            {transaction.asset_amount?.asset?.name ??
                              transaction.asset_amount?.asset?.code ??
                              "—"}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: TEXT_MUTED }}>
                            {transaction.asset_amount?.asset?.code ?? ""}
                          </Typography>
                        </Box>
                      </Stack>
                    </BodyCell>
                    <BodyCell sx={{ color: TEXT_SECONDARY }}>
                      {typeLabel(transaction.type)}
                    </BodyCell>
                    <BodyCell>{formatUnits(transaction.asset_amount?.amount)}</BodyCell>
                    <BodyCell sx={{ color: TEXT_SECONDARY }}>
                      {formatUtcDateTime(transaction.datetime)}
                    </BodyCell>
                    <BodyCell>
                      {transaction.missing_cost_basis ? (
                        <Typography sx={{ fontSize: 13, color: SALMON, fontWeight: 500 }}>
                          Missing
                        </Typography>
                      ) : isTransferOut(transaction.type) && !transaction.cost_basis ? null : (
                        <>
                          <Typography sx={{ fontSize: 14, color: NAVY }}>
                            {formatUsd(transaction.cost_basis?.amount)}
                          </Typography>
                          {transaction.gain && (
                            <Typography sx={{ fontSize: 12, color: TEXT_MUTED }}>
                              {`Proceeds ${formatUsd(
                                transaction.proceeds?.amount,
                              )} · Gain ${signedUsd(transaction.gain.amount)}`}
                            </Typography>
                          )}
                        </>
                      )}
                    </BodyCell>
                    <BodyCell align="right">
                      {canReview &&
                        (activeTab === "needs-review" ? (
                          <ContainedButton
                            color="salmon"
                            width="130px"
                            height="36px"
                            borderRadius="999px"
                            fontWeight={500}
                            onClick={() => setSelectedTransaction(transaction)}
                          >
                            Declare cost
                          </ContainedButton>
                        ) : (
                          <ButtonBase
                            onClick={() => setSelectedTransaction(transaction)}
                            sx={{ fontSize: 13, fontWeight: 500, color: SALMON }}
                          >
                            Edit
                          </ButtonBase>
                        ))}
                    </BodyCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 2 }}
      >
        <Typography sx={{ fontSize: 13, color: TEXT_MUTED }}>
          {`Showing ${pageTransactions.length} of ${tabTransactions.length} transactions · sorted newest first`}
        </Typography>
        {pageCount > 1 && (
          <Stack direction="row" gap={0.75}>
            {Array.from({ length: pageCount }).map((_, idx) => (
              <PageButton key={idx} active={idx === currentPage} onClick={() => setPage(idx)}>
                {idx + 1}
              </PageButton>
            ))}
          </Stack>
        )}
      </Stack>

      {selectedTransaction && (
        <ProvideCostBasisModal
          key={selectedTransaction.id}
          transaction={selectedTransaction}
          onClose={closeReviewModal}
          onSubmitted={handleSubmitted}
          onReplacementFailed={handleReplacementFailed}
        />
      )}
    </Card>
  );
};

export default TaxTransactionReviewTable;
